import { request, requestEmpty } from './client'

/**
 * Building the inventory rather than only reading it.
 *
 * Every field here is optional except an address, and that is the design and not
 * an oversight. An object is entered while somebody is on the phone or standing
 * in a hallway, and the shape of the place is often not known yet. A form that
 * demands the whole thing is one people abandon or fill in with guesses, and a
 * guess is worse than a gap: unknown blocks "within budget" and a zero does not.
 *
 * So nothing unstated is sent. `undefined` fields are dropped before the request
 * goes out, which is what leaves them unknown on the other side; sending `null`
 * is how a field is cleared on purpose.
 */

export interface NewProperty {
  readonly address_normalized: string
  readonly address_exact?: string
  readonly area?: string
  readonly municipality?: string
  readonly floor?: number | null
  readonly elevator?: boolean | null
  readonly heating?: string
  readonly notes?: string
}

export type UnitKind = 'apartment' | 'room' | 'bed'

export interface NewUnit {
  readonly kind: UnitKind
  /** The space this one is inside: a room's flat, a place's room. */
  readonly parent_unit_id?: string | null
  readonly label?: string
  readonly bedrooms?: number | null
  readonly beds?: number | null
  readonly bathrooms?: number | null
  readonly capacity?: number | null
  /** Floor area in square metres. Absent stays unmeasured, never zero. */
  readonly area_sqm?: number | null
  readonly room_private?: boolean | null
  readonly furnished?: boolean | null
  readonly notes?: string
}

export interface NewListing {
  readonly title?: string
  readonly source_type?: 'private' | 'agent' | 'organization' | 'unknown'
  readonly currency?: string
  readonly notes?: string
}

interface Created {
  readonly id: string
}

function created(body: unknown): Created {
  if (typeof body !== 'object' || body === null || typeof (body as Created).id !== 'string') {
    throw new Error('the server did not say what it created')
  }
  return { id: (body as Created).id }
}

/** Strip the keys nobody filled in, so they stay unknown rather than becoming empty. */
function stated<T extends object>(input: T): Partial<T> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined && value !== '') out[key] = value
  }
  return out as Partial<T>
}

export async function createProperty(input: NewProperty): Promise<Created> {
  return request('/api/properties', created, { method: 'POST', body: stated(input) })
}

export async function updateProperty(id: string, input: Partial<NewProperty>): Promise<void> {
  return requestEmpty(`/api/properties/${id}`, { method: 'PATCH', body: input })
}

export async function deleteProperty(id: string): Promise<void> {
  return requestEmpty(`/api/properties/${id}`, { method: 'DELETE' })
}

export async function createUnit(propertyID: string, input: NewUnit): Promise<Created> {
  return request(`/api/properties/${propertyID}/units`, created, {
    method: 'POST',
    body: stated(input),
  })
}

export async function updateUnit(id: string, input: Partial<NewUnit>): Promise<void> {
  return requestEmpty(`/api/units/${id}`, { method: 'PATCH', body: input })
}

export async function deleteUnit(id: string): Promise<void> {
  return requestEmpty(`/api/units/${id}`, { method: 'DELETE' })
}

export async function createListing(unitID: string, input: NewListing): Promise<Created> {
  return request(`/api/units/${unitID}/listings`, created, { method: 'POST', body: stated(input) })
}

export async function deleteListing(id: string): Promise<void> {
  return requestEmpty(`/api/listings/${id}`, { method: 'DELETE' })
}

/**
 * The statuses a person may set from the panel.
 *
 * All ten of them, and in any direction: the owner decided that the arrows of
 * the state machine describe how a listing moves on its own, and somebody
 * standing in the flat knows things the arrows do not. What the code still
 * insists on is that the move is recorded and that any drafts quoting the
 * listing are retired with it.
 */
export const listingStatuses = [
  'verified',
  'rented',
  'reserved',
  'on_hold',
  'withdrawn',
  'stale',
  'imported',
  'contact_pending',
  'rejected',
  'duplicate',
] as const

export type ListingStatus = (typeof listingStatuses)[number]

export async function setListingStatus(
  id: string,
  status: ListingStatus,
  reason: string,
): Promise<void> {
  return requestEmpty(`/api/listings/${id}/status`, {
    method: 'POST',
    body: { status, reason },
  })
}

export interface UploadedMedia {
  readonly id: string
  readonly kind: string
  /**
   * True for the formats browsers disagree about -- an iPhone's .mov, a .heic
   * photograph. Nothing transcodes, so saying so at the upload is the only thing
   * that can be done about it, and it is better than a client finding out.
   */
  readonly may_not_play_everywhere: boolean
}

function uploaded(body: unknown): UploadedMedia {
  const value = body as Partial<UploadedMedia>
  if (typeof value?.id !== 'string') throw new Error('the server did not say what it stored')
  return {
    id: value.id,
    kind: typeof value.kind === 'string' ? value.kind : 'other',
    may_not_play_everywhere: value.may_not_play_everywhere === true,
  }
}

export async function uploadMedia(
  listingID: string,
  file: File,
  caption?: string,
): Promise<UploadedMedia> {
  const form = new FormData()
  form.append('file', file)
  if (caption !== undefined && caption !== '') form.append('caption', caption)

  return request(`/api/listings/${listingID}/media`, uploaded, { method: 'POST', body: form })
}

export async function deleteMedia(id: string): Promise<void> {
  return requestEmpty(`/api/media/${id}`, { method: 'DELETE' })
}

/** Where the browser reads a stored file. It is behind the session, like everything else. */
export function mediaURL(id: string): string {
  return `/api/media/${id}`
}

/**
 * What an offer costs.
 *
 * `amount` is in whole euro as a person types it, and its absence is the whole
 * design: a charge with no figure is unknown, which blocks "within budget" and
 * reaches a person. A charge sent as `0` is one somebody said is nothing, which
 * is a different and rarer claim, and the two must never collapse into each
 * other (invariant 2).
 */
export interface PriceComponentInput {
  readonly component: string
  readonly amount?: number
  readonly estimated?: boolean
  /** "Two months' deposit" as it was said. The figure follows a rent that moves. */
  readonly months?: number
  /** How many people this price is for. Absent means it does not depend on that. */
  readonly for_occupants?: number
  readonly note?: string
}

/** The charges, in the order a person thinks about them. */
export const monthlyComponents = [
  'rent',
  'condominio',
  'fixed_bills',
  'variable_bills',
  'other_recurring',
] as const

export const upfrontComponents = [
  'deposit',
  'first_month',
  'prepayment',
  'agency_fee',
  'romaready_fee',
  'one_time',
] as const

export async function replacePrices(
  listingID: string,
  components: readonly PriceComponentInput[],
): Promise<void> {
  return requestEmpty(`/api/listings/${listingID}/prices`, {
    method: 'PUT',
    body: { components },
  })
}

export interface AvailabilityWindowInput {
  /** The first free day, YYYY-MM-DD. */
  readonly from: string
  /** The last day covered — not the day the next tenant arrives. Empty is open-ended. */
  readonly to?: string
  readonly min_months?: number
  readonly max_months?: number
  readonly renewable?: boolean
  readonly flexibility_days?: number
  readonly note?: string
}

export async function replaceAvailability(
  listingID: string,
  windows: readonly AvailabilityWindowInput[],
): Promise<void> {
  return requestEmpty(`/api/listings/${listingID}/availability`, {
    method: 'PUT',
    body: { windows },
  })
}

/**
 * What an object still needs from the landlord, and the button that says it
 * arrived.
 *
 *   PUT /api/listings/{id}/info-needed    listings.write
 *
 * One call for both directions. An empty string clears the flag; anything else
 * raises it with those words. A screen that could raise a mark it has no way to
 * remove is how a list of "waiting on the landlord" fills with things that came
 * back weeks ago.
 */
export async function setInfoNeeded(listingId: string, needed: string): Promise<void> {
  return requestEmpty(`/api/listings/${encodeURIComponent(listingId)}/info-needed`, {
    method: 'PUT',
    body: { needed },
  })
}

/**
 * What somebody read off the contract.
 *
 *   PATCH /api/listings/{id}/terms    listings.write
 *
 * The matcher refuses to offer a flat whose contract type nobody has recorded —
 * no legal conclusion is ours to draw — so until this existed every object
 * entered by hand was withheld from every client for a fact the panel had no way
 * to supply.
 */
export async function saveTerms(listingID: string, terms: Record<string, unknown>): Promise<void> {
  return requestEmpty(`/api/listings/${encodeURIComponent(listingID)}/terms`, {
    method: 'PATCH',
    body: terms,
  })
}
